import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import AgreementCompanyForm from "@/modules/agreement-companies/components/AgreementCompanyForm";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useAgreementCompaniesDictionary } from "@/modules/agreement-companies/hooks/useAgreementCompaniesDictionary";
import { ApiError } from "@/services/api";
import {
  AgreementCompany,
  AgreementCompanyCreate,
} from "@/types/agreementCompany";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";

export default function AgreementCompanyCreator() {
  const id = "new";
  const { t } = useTranslation();
  const config = useAgreementCompaniesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invalidateListQuery } = useQueryCacheUpdater(
    Module.AgreementCompanies,
  );
  const labels = config.getLabels(t);
  const { can_create } = useModulePermissions();

  const mutationFn: MutationFunction<
    AgreementCompany,
    AgreementCompanyCreate
  > = async (values) => {
    const response = await config.service.post(values);
    return response;
  };

  const mutation = useMutation<
    AgreementCompany,
    ApiError,
    AgreementCompanyCreate
  >({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<AgreementCompanyCreate>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<AgreementCompanyCreate>,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <AgreementCompanyForm />
    </DictionaryRecordContextProvider>
  );
}
